/**
 * Ensures a URL has a proper protocol (http:// or https://)
 * If no protocol is present, defaults to https://
 *
 * @param url - The URL to format
 * @returns The URL with a protocol, or the original URL if it already has one
 *
 * @example
 * ensureProtocol('google.com') // 'https://google.com'
 * ensureProtocol('www.example.com') // 'https://www.example.com'
 * ensureProtocol('https://already-has-protocol.com') // 'https://already-has-protocol.com'
 * ensureProtocol('http://insecure-but-valid.com') // 'http://insecure-but-valid.com'
 */
export const ensureProtocol = (url: string): string => {
  if (!url) return url;

  // If it already has a protocol, return as-is
  if (url.match(/^https?:\/\//i)) {
    return url;
  }

  // Add https:// if no protocol is present
  return `https://${url}`;
};

/**
 * Validates if a string is a properly formatted URL
 *
 * @param url - The URL to validate
 * @returns true if the URL is valid, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(ensureProtocol(url));
    return true;
  } catch {
    return false;
  }
};

/**
 * Extracts the domain from a URL
 *
 * @param url - The URL to extract domain from
 * @returns The domain or null if invalid URL
 */
export const extractDomain = (url: string): string | null => {
  try {
    const urlObj = new URL(ensureProtocol(url));
    return urlObj.hostname;
  } catch {
    return null;
  }
};
